"use client";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useState, useRef, useEffect } from "react";
import ModuleCard from "./ModuleCard";

// 模块数据接口定义
interface Module {
  titleKey: string; // 翻译文本的key
  imageUrl: string; // 图片URL
}

// 组件props接口
interface ModuleGridProps {
  modules: Module[]; // 模块数组
}

/**
 * 模块网格组件 - 在桌面端显示为3x2网格，在移动端显示为可拖拽的水平滚动列表
 * 功能特点：
 * - 响应式设计：桌面端网格布局，移动端走马灯布局
 * - 丝滑拖拽：支持鼠标拖拽和惯性滚动
 * - 无极滚动：不强制吸附到特定位置
 * - 智能指示器：根据滚动位置动态显示当前进度
 */
export default function ModuleGrid({ modules }: ModuleGridProps) {
  const theme = useTheme();
  // 判断是否为移动端（屏幕宽度小于md断点）
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  // 当前活跃的指示器索引（用于显示滚动进度）
  const [currentIndex, setCurrentIndex] = useState(0);
  // 滚动容器的引用
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // === 鼠标拖拽相关状态管理 ===
  const [isDragging, setIsDragging] = useState(false); // 是否正在拖拽
  const [startX, setStartX] = useState(0); // 拖拽开始时的X坐标
  const [scrollLeft, setScrollLeft] = useState(0); // 拖拽开始时的滚动位置
  const [velocity, setVelocity] = useState(0); // 拖拽速度（用于惯性滚动）
  const [lastX, setLastX] = useState(0); // 上一次鼠标位置
  const [lastTime, setLastTime] = useState(0); // 上一次时间戳
  const animationFrameRef = useRef<number | null>(null); // 动画帧引用（用于惯性滚动）

  // 常量定义
  const CARD_WIDTH_REM = 22.5;
  const GAP_REM = 2;
  const CARD_WIDTH_PX = CARD_WIDTH_REM * 16; // 22.5rem = 360px
  const GAP_PX = GAP_REM * 8; // 2 = 16px
  const ITEM_WIDTH_PX = CARD_WIDTH_PX + GAP_PX;

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const maxScroll = scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth;
      
      // 根据滚动比例计算当前应该高亮的指示器
      const scrollRatio = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      const newIndex = Math.round(scrollRatio * (modules.length - 1));
      setCurrentIndex(Math.max(0, Math.min(newIndex, modules.length - 1)));
    }
  };

  const scrollToIndex = (index: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        left: index * ITEM_WIDTH_PX,
        behavior: "smooth",
      });
      setCurrentIndex(index);
    }
  };

  // 鼠标拖拽事件处理
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    setStartX(x);
    setLastX(x);
    setLastTime(Date.now());
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    setVelocity(0);
    e.preventDefault();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const currentTime = Date.now();
    const deltaX = x - lastX;
    const deltaTime = currentTime - lastTime;
    
    // 计算速度用于惯性滚动
    if (deltaTime > 0) {
      setVelocity(deltaX / deltaTime);
    }
    
    const walk = x - startX;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    
    setLastX(x);
    setLastTime(currentTime);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    
    // 添加惯性滚动，但不自动吸附
    if (Math.abs(velocity) > 0.1 && scrollContainerRef.current) {
      let currentVelocity = velocity * 300; // 放大速度效果
      const friction = 0.95; // 摩擦系数
      
      const inertiaScroll = () => {
        if (Math.abs(currentVelocity) < 1 || !scrollContainerRef.current) {
          return;
        }
        
        scrollContainerRef.current.scrollLeft -= currentVelocity;
        currentVelocity *= friction;
        
        animationFrameRef.current = requestAnimationFrame(inertiaScroll);
      };
      
      animationFrameRef.current = requestAnimationFrame(inertiaScroll);
    }
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  // 清理动画帧
  const cleanupAnimation = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      cleanupAnimation();
    };
  }, []);

  if (isMobile) {
    return (
      <Box sx={{ mt: 6, mb: "7.5rem", mx: "1.5rem" }}>
        {/* 走马灯容器 */}
        <Box
          ref={scrollContainerRef}
          onScroll={handleScroll}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          sx={{
            display: "flex",
            overflowX: "auto",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            scrollbarWidth: "none",
            gap: 2,
            mb: 3,
            cursor: isDragging ? "grabbing" : "grab",
            userSelect: "none",
          }}>
          {modules.map((mod, index) => (
            <Box
              key={mod.titleKey}
              sx={{
                minWidth: `${CARD_WIDTH_REM}rem`,
                maxWidth: `${CARD_WIDTH_REM}rem`,
                position: "relative",
                "&:hover": {
                  zIndex: 10,
                },
              }}>
              <ModuleCard titleKey={mod.titleKey} imageUrl={mod.imageUrl} />
            </Box>
          ))}
        </Box>

        {/* 指示器 */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
          }}>
          {modules.map((_, index) => (
            <Box
              key={index}
              onClick={() => scrollToIndex(index)}
              sx={{
                width: currentIndex === index ? "2rem" : "0.5rem",
                height: "0.25rem",
                borderRadius: "0.125rem",
                backgroundColor: currentIndex === index ? "primary.main" : "divider",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: currentIndex === index ? "primary.main" : "text.secondary",
                },
              }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  // 桌面端网格布局
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gridTemplateRows: "repeat(2, 1fr)",
        gap: 4,
        mt: 6,
        mb: "7.5rem",
      }}>
      {modules.map((mod) => (
        <Box
          key={mod.titleKey}
          sx={{
            position: "relative",
            "&:hover": {
              zIndex: 10,
            },
          }}>
          <ModuleCard titleKey={mod.titleKey} imageUrl={mod.imageUrl} />
        </Box>
      ))}
    </Box>
  );
}
